<script lang="ts">
  import { defang } from '$lib/protocol/link-guard';
  import type { StyleRange, TextElement } from '$lib/protocol/types';

  let { element }: { element: TextElement } = $props();

  interface Span {
    text: string;
    bold: boolean;
    italic: boolean;
    listItem: boolean;
  }

  // Formatting ranges are given in CODE POINTS alongside plain text — the same
  // index must point at the same character in every implementation, so the text
  // is split into code points before slicing. Unknown styles are skipped; the
  // defang pass keeps addresses visible but never tappable.
  function spans(el: TextElement): Span[] {
    const points = [...el.text];
    if (el.formatting.length === 0) {
      return [{ text: defang(el.text), bold: false, italic: false, listItem: false }];
    }
    const bounds = new Set<number>([0, points.length]);
    for (const range of el.formatting) {
      bounds.add(Math.max(0, Math.min(points.length, range.start)));
      bounds.add(Math.max(0, Math.min(points.length, range.end)));
    }
    const sorted = [...bounds].sort((a, b) => a - b);
    const out: Span[] = [];
    for (let i = 0; i < sorted.length - 1; i++) {
      const start = sorted[i]!;
      const end = sorted[i + 1]!;
      const covering = el.formatting.filter((r: StyleRange) => r.start <= start && r.end >= end);
      out.push({
        text: defang(points.slice(start, end).join('')),
        bold: covering.some((r) => r.style === 'bold'),
        italic: covering.some((r) => r.style === 'italic'),
        listItem: covering.some((r) => r.style === 'list_item'),
      });
    }
    return out.filter((s) => s.text.length > 0);
  }

  const parts = $derived(spans(element));
</script>

<p class="text">
  {#each parts as part, i (i)}
    {#if part.listItem}<span class="li">•&nbsp;</span>{/if}<span
      class:bold={part.bold}
      class:italic={part.italic}>{part.text}</span
    >
  {/each}
</p>

<style>
  .text {
    margin: 0;
    white-space: pre-wrap;
    overflow-wrap: anywhere;
  }
  .bold {
    font-weight: 700;
  }
  .italic {
    font-style: italic;
  }
  .li {
    color: var(--ink-soft);
  }
</style>
